import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Character, Element, Pillar } from '../types';
import { charactersAPI, elementsAPI, pillarsAPI } from '../services/api';
import { useOptimizedImage } from '../hooks/useOptimizedImage';
import { useDynamicFontSize } from '../hooks/useDynamicFontSize';
import { getImageSource } from '../utils/imageUtils';
import OptimizedImage from './OptimizedImage';
import LoadingOverlay from './LoadingOverlay';
import './CardDetails.css';

interface CardDetailsProps {
  cardType: 'character' | 'element' | 'pillar';
}

const CardDetails: React.FC<CardDetailsProps> = ({ cardType }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [card, setCard] = useState<Character | Element | Pillar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [imageKey, setImageKey] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);

  // Dynamic font size for title
  const titleRef = useDynamicFontSize<HTMLHeadingElement>({
    minFontSize: 2,
    maxFontSize: 4,
    step: 0.1,
    containerPadding: 40,
    maxHeight: 100
  });

  // Card loading hook - use consistent image loading pattern
  const imageSrc = card ? getImageSource(
    card as any, 
    cardType, 
    imageKey
  ) : '';
  
  const {
    cardRef,
    overlayRef,
    loadingState = { isLoading: false, progress: 0, error: false }
  } = useOptimizedImage({
    src: imageSrc,
    alt: card?.name || '',
    withCardOverlay: false
  });

  // Load card data based on type
  useEffect(() => {
    const loadCard = async () => {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        let cardData;
        
        switch (cardType) {
          case 'character':
            cardData = await charactersAPI.getBySlug(slug);
            break;
          case 'element':
            cardData = await elementsAPI.getBySlug(slug);
            break;
          case 'pillar':
            cardData = await pillarsAPI.getBySlug(slug);
            break;
          default:
            throw new Error('Invalid card type');
        }
        
        setCard(cardData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load card details');
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [slug, cardType]);

  // Get available tabs based on card type
  const getAvailableTabs = () => {
    if (!card) return [];
    
    switch (cardType) {
      case 'character':
        return [
          { id: 'summary', label: 'Summary' },
          { id: 'features', label: 'Features' },
          { id: 'attributes', label: 'Attributes' },
          { id: 'equipment', label: 'Equipment' },
          { id: 'threads', label: 'Threads' }
        ];
      case 'element':
        return [
          { id: 'summary', label: 'Summary' },
          { id: 'features', label: 'Features' },
          { id: 'attributes', label: 'Attributes' },
          { id: 'links', label: 'Links' },
          { id: 'characters', label: 'Characters' }
        ];
      case 'pillar':
        return [
          { id: 'summary', label: 'Summary' },
          { id: 'article', label: 'Article' },
          { id: 'ancestors', label: 'Ancestors' },
          { id: 'children', label: 'Children' },
          { id: 'elements', label: 'Elements' }
        ];
      default:
        return [];
    }
  };

  // Render content based on active tab and card type
  const renderTabContent = () => {
    if (!card) return null;

    switch (activeTab) {
      case 'summary':
        return (
          <div className="tab-content">
            <div className="content-grid">
              <div className="left-content">
                <div className="description-section">
                  <h4>Description</h4>
                  <div className="description-text">
                    {card.description || 'No description available.'}
                  </div>
                </div>
                {card.article && (
                  <div className="article-section">
                    <h4>Article</h4>
                    <div className="article-text">
                      {card.article}
                    </div>
                  </div>
                )}
              </div>
              <div className="right-content">
                {renderCardSpecificFields()}
              </div>
            </div>
          </div>
        );
      
      case 'features':
        return (
          <div className="tab-content">
            <div className="features-text">
              {(card as any).features || 'No features available.'}
            </div>
          </div>
        );
      
      case 'attributes':
        return (
          <div className="tab-content">
            <div className="attributes-text">
              {(card as any).attributes || 'No attributes available.'}
            </div>
          </div>
        );
      
      case 'equipment':
        return cardType === 'character' ? renderEquipmentTab() : null;     
                
      case 'links':
        return cardType === 'element' ? renderLinksTab() : null;
      
      case 'characters':
        return cardType === 'element' ? renderCharactersTab() : null;
      
      case 'article':
        return cardType === 'pillar' ? renderArticleTab() : null;
      
      case 'ancestors':
        return cardType === 'pillar' ? renderAncestorsTab() : null;
      
      case 'children':
        return cardType === 'pillar' ? renderChildrenTab() : null;
      
      case 'elements':
        return cardType === 'pillar' ? renderElementsTab() : null;
      
      case 'threads':
        return cardType === 'character' ? renderThreadsTab() : null;
      
      default:
        return null;
    }
  };

  // Render card-specific fields for summary tab
  const renderCardSpecificFields = () => {
    if (!card) return null;

    switch (cardType) {
      case 'character':
        return renderCharacterFields();
      case 'element':
        return renderElementFields();
      case 'pillar':
        return renderPillarFields();
      default:
        return null;
    }
  };

  const renderCharacterFields = () => {
    const character = card as Character;
    return (
      <div className="character-fields">
        {/* <div className="field-group">
          <h4>Difficulty</h4>
          <span className="field-value">{character.difficulty}</span>
        </div>
        <div className="field-group">
          <h4>Duration</h4>
          <span className="field-value">{character.duration} days</span>
        </div>
        <div className="field-group">
          <h4>Max Group Size</h4>
          <span className="field-value">{character.maxGroupSize}</span>
        </div>
        <div className="field-group">
          <h4>Price</h4>
          <span className="field-value">${character.price}</span>
        </div>
        <div className="field-group">
          <h4>Rating</h4>
          <span className="field-value">{character.ratingsAverage}/5 ({character.ratingsQuantity} reviews)</span>
        </div> */}
      </div>
    );
  };

  const renderElementFields = () => {
    const element = card as Element;
    return (
      <div className="element-fields">
        {element.category && (
          <div className="field-group">
            <h4>Category</h4>
            <span className="field-value">{element.category}</span>
          </div>
        )}
        {element.rarity && (
          <div className="field-group">
            <h4>Rarity</h4>
            <span className="field-value">{element.rarity}</span>
          </div>
        )}
      </div>
    );
  };

  const renderPillarFields = () => {
    const pillar = card as Pillar;
    return (
      <div className="pillar-fields">
        {pillar.L1pillar && (
          <div className="field-group">
            <h4>L1 Pillar</h4>
            <span className="field-value">{pillar.L1pillar}</span>
          </div>
        )}
        {pillar.levelFromL1 && (
          <div className="field-group">
            <h4>Level from L1</h4>
            <span className="field-value">{pillar.levelFromL1}</span>
          </div>
        )}
        {pillar.level && (
          <div className="field-group">
            <h4>Level</h4>
            <span className="field-value">{pillar.level}</span>
          </div>
        )}
      </div>
    );
  };

  // Tab-specific render methods
  const renderEquipmentTab = () => {
    const character = card as Character;
    return (
      <div className="equipment-tab">
        <div className="equipment-grid">
          <div className="equipment-section">
            <h4>Weapons</h4>
            <ul className="equipment-list">
              {character.weapons?.map(weapon => (
                <li key={weapon._id}>
                  <span className="equipment-item">{weapon.name}</span>
                </li>
              )) || <li>No weapons</li>}
            </ul>
          </div>
          <div className="equipment-section">
            <h4>Armor</h4>
            <ul className="equipment-list">
              {character.armor?.map(armor => (
                <li key={armor._id}>
                  <span className="equipment-item">{armor.name}</span>
                </li>
              )) || <li>No armor</li>}
            </ul>
          </div>
          <div className="equipment-section">
            <h4>Items</h4>
            <ul className="equipment-list">
              {character.items?.map(item => (
                <li key={item._id}>
                  <span className="equipment-item">{item.name}</span>
                </li>
              )) || <li>No items</li>}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderLinksTab = () => {
    const element = card as Element;
    return (
      <div className="links-tab">
        <ul className="links-list">
          {element.links?.map(link => (
            <li key={link._id}>
              <span className="link-item">{link.name}</span>
            </li>
          )) || <li>No links</li>}
        </ul>
      </div>
    );
  };

  const renderCharactersTab = () => {
    const element = card as Element;
    return (
      <div className="characters-tab">
        <div className="characters-grid">
          {element.weaponsOf && element.weaponsOf.length > 0 && (
            <div className="character-section">
              <h4>Used as Weapon by</h4>
              <ul className="character-list">
                {element.weaponsOf.map(character => (
                  <li key={character._id}>
                    <span className="character-item">{character.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {element.armorOf && element.armorOf.length > 0 && (
            <div className="character-section">
              <h4>Used as Armor by</h4>
              <ul className="character-list">
                {element.armorOf.map(character => (
                  <li key={character._id}>
                    <span className="character-item">{character.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {element.itemsOf && element.itemsOf.length > 0 && (
            <div className="character-section">
              <h4>Carried as Item by</h4>
              <ul className="character-list">
                {element.itemsOf.map(character => (
                  <li key={character._id}>
                    <span className="character-item">{character.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderArticleTab = () => {
    const pillar = card as Pillar;
    return (
      <div className="article-tab">
        <div className="article-content">
          {pillar.article || 'No article content available.'}
        </div>
      </div>
    );
  };

  const renderAncestorsTab = () => {
    const pillar = card as Pillar;
    return (
      <div className="ancestors-tab">
        <ul className="ancestors-list">
          {pillar.ancestors?.map(ancestor => (
            <li key={ancestor._id}>
              <span className="ancestor-item">{ancestor.name} (Level {ancestor.level})</span>
            </li>
          )) || <li>No ancestors</li>}
        </ul>
      </div>
    );
  };

  const renderChildrenTab = () => {
    const pillar = card as Pillar;
    return (
      <div className="children-tab">
        <ul className="children-list">
          {pillar.children?.map(child => (
            <li key={child._id}>
              <span className="child-item">{child.name} (Level {child.level})</span>
            </li>
          )) || <li>No children</li>}
        </ul>
      </div>
    );
  };

  const renderElementsTab = () => {
    const pillar = card as Pillar;
    return (
      <div className="elements-tab">
        <ul className="elements-list">
          {pillar.elements?.map(element => (
            <li key={element._id}>
              <span className="element-item">{element.name}</span>
            </li>
          )) || <li>No elements</li>}
        </ul>
      </div>
    );
  };

  const renderThreadsTab = () => {
    return (
      <div className="threads-tab">
        <div className="threads-content">
          <p>Threads functionality coming soon...</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card-details-loading">
        <LoadingOverlay isLoading={true} message={`Loading ${cardType} details...`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-details-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate(-1)} className="btn btn--green">
          Go Back
        </button>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="card-details-not-found">
        <h2>{cardType.charAt(0).toUpperCase() + cardType.slice(1)} Not Found</h2>
        <p>The {cardType} you're looking for doesn't exist.</p>
        <button onClick={() => navigate(-1)} className="btn btn--green">
          Go Back
        </button>
      </div>
    );
  }

  const availableTabs = getAvailableTabs();

  return (
    <div className="card-details" ref={cardRef}>
      {/* Card loading overlay */}
      <div ref={overlayRef} />
      
      <div className="card-details-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← Back
        </button>
        <h1 ref={titleRef} className="card-title">
          {card.name}
        </h1>
      </div>

      <div className="card-details-content">
        <div className="card-image-section">
          <OptimizedImage
            src={imageSrc}
            alt={card.name}
            className="card-main-image"
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
          />
        </div>

        <div className="card-info-section">
          <div className="tab-navigation">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content-container">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDetails; 